import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4FireSmokeArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'Does Renters Insurance Cover Fire and Smoke Damage? | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Learn when HO-4 renters insurance covers fire and smoke damage to your belongings, temporary housing, and liability after a kitchen or building fire.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/does-renters-insurance-cover-fire-and-smoke-damage"
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/knowledge-base')} style={{ marginBottom: '1rem' }}>
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>Does renters insurance cover fire and smoke damage?</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              Fire is one of the core perils HO-4 is built for — but smoke, soot, and displacement costs have their own limits and rules.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>Last modified: {HO4_LAST_MODIFIED}</p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                Yes — standard HO-4 renters insurance typically covers fire and smoke damage to your personal belongings,
                liability if you accidentally cause a fire, and{' '}
                <Link to="/knowledge-base/what-is-loss-of-use-renters-insurance" style={articleLinkStyle}>
                  loss of use
                </Link>{' '}
                if a covered fire makes your unit uninhabitable. The building structure itself is the landlord&apos;s
                responsibility, not yours.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What fire and smoke coverage usually pays for</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Furniture, clothing, electronics, and other belongings destroyed or damaged by fire.</li>
                <li style={{ marginBottom: '0.5rem' }}>Smoke and soot damage to personal property — even when flames did not reach your unit.</li>
                <li style={{ marginBottom: '0.5rem' }}>Additional living expenses if you must stay elsewhere during repairs.</li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Personal liability if you accidentally start a kitchen fire or leave a candle unattended.
                </li>
                <li>Medical payments for guests injured in a fire you caused, up to your policy limit.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Common exclusions and limits</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Intentional fires or arson by the policyholder.</li>
                <li style={{ marginBottom: '0.5rem' }}>Damage to the building structure, walls, or fixtures — your landlord&apos;s insurance handles that.</li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Sub-limits on high-value items like jewelry unless you add a scheduled personal property endorsement.
                </li>
                <li>
                  Your deductible applies before the insurer pays — see{' '}
                  <Link to="/knowledge-base/what-deductible-should-i-choose-for-ho4" style={articleLinkStyle}>
                    choosing a deductible
                  </Link>.
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>After a fire: claims checklist</h3>
              <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Ensure everyone is safe and call 911 — do not re-enter until cleared.</li>
                <li style={{ marginBottom: '0.5rem' }}>Notify your landlord and insurer as soon as possible.</li>
                <li style={{ marginBottom: '0.5rem' }}>Photograph damage before cleanup and keep a written inventory of destroyed items.</li>
                <li style={{ marginBottom: '0.5rem' }}>Save receipts for temporary housing, meals, and replacement essentials.</li>
                <li>
                  Follow your insurer&apos;s{' '}
                  <Link to="/knowledge-base/how-to-file-a-renters-insurance-claim" style={articleLinkStyle}>
                    claims process
                  </Link>{' '}
                  and ask about ACV vs. replacement cost settlement.
                </li>
              </ol>
            </div>
          </article>

          <section className="card mb-4" style={{ borderColor: 'var(--color-green)', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>Get HO-4 coverage that protects your belongings and liability after fire, theft, and other covered perils.</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/qualify')} style={{ fontWeight: 700, paddingInline: '1.4rem' }}>Get started now</button>
            </div>
          </section>
          <section className="card" style={{ borderColor: '#FBBF24', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Questions about fire or smoke claims?</h3>
              <p style={{ marginBottom: '1rem' }}>An agent can explain loss of use limits, replacement cost options, and liability coverage on your policy.</p>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/help')} style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}>Speak to an agent</button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4FireSmokeArticlePage;
